import React from 'react';
import Suggest from 'react-geosuggest';
import styled from 'styled-components';
import { Form, Button, Popup, Icon } from 'semantic-ui-react';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { style } from './style';
import ErrorMessage from '../ErrorMessage';
import VoE from '../../../../shared/lib/ValidOrEmpty';
import Required from '../Required';
import AsyncSelect from '../AsyncSelect';
import { ComuniService, NazioniService, ComuniNazioniService } from '../../../../shared/services/rpc/comune';

import GeoInput from '../GeoInput';

const StyledSuggest = styled(Suggest)`
  ${style}
`;

const SwitchMode = (props) => (
  <Button.Group basic style={{ float: 'right', position: 'relative', top: -2 }}>
    <Button
      size="mini"
      active={props.mode === 'auto'}
      onClick={() => props.switch('auto')}
      style={{ fontSize: 8 }}
      type="button"
      icon="search"
    ></Button>
    <Button
      size="mini"
      active={props.mode === 'manual'}
      onClick={() => props.switch('manual')}
      style={{ fontSize: 8 }}
      type="button"
      icon="content"
    ></Button>
  </Button.Group>
);

SwitchMode.propTypes = {
  switch: PropTypes.func,
  mode: PropTypes.string,
};

export const SuggestComuneNascita = ({
  onChange,
  onBlur,
  touched,
  error,
  name,
  label,
  initialValue,
  required,
  tooltip,
  disabled = false,
}) => {
  const handleBlur = () => {
    onBlur('comuneNascita', true);
  };

  const getOptions = (input, cb) => {
    ComuniNazioniService.getComuniNazioniOptions(input || initialValue)
      .then((res) => {
        cb(null, res);
      })
      .catch(
        (err) => console.log('ERRORE COMUNI', err) // eslint-disable-line
      );
  };

  const onAsyncChange = (field, obj) => {
    // console.log(field, obj);
    const country = obj.provincia === 'EE';
    if (country) {
      onChange('comuneNascita', obj.comune);
      onChange('provinciaNascita', obj.provincia);
      onChange('regioneNascita', '');
    } else {
      onChange('comuneNascita', obj.comune);
      onChange('provinciaNascita', obj.provincia);
      onChange('regioneNascita', obj.regione);
    }
  };

  return (
    <Form.Field error={Boolean(touched && error)}>
      <label htmlFor={name}>
        {label}
        <Required required={required} />{' '}
        {tooltip ? (
          <Popup
            className="fieldTooltip"
            style={{ backgroundColor: '#333d68' }}
            inverted
            trigger={<Icon size="small" circular name="question" />}
          >
            {tooltip}
          </Popup>
        ) : (
          ''
        )}
      </label>
      <AsyncSelect
        key={initialValue}
        loadOptions={_.debounce(getOptions, 250)}
        value={{ label: initialValue, value: initialValue }}
        onChange={onAsyncChange}
        onBlur={handleBlur}
        error={error}
        touched={touched}
        name="comune"
        disabled={disabled}
      />
      {touched && error && <ErrorMessage>{error}</ErrorMessage>}
    </Form.Field>
  );
};

export const ComuneNascitaCustomField = ({
  onChange,
  onBlur,
  touched,
  error,
  name,
  label,
  initialValue,
  required,
  tooltip,
  disabled = false,
  provincia,
}) => {
  const handleBlur = () => {
    onBlur(name, true);
  };

  const getOptions = (input, cb) => {
    ComuniNazioniService.getComuniNazioniOptions(input || initialValue)
      .then((res) => {
        cb(null, res);
      })
      .catch(
        (err) => console.log('ERRORE COMUNI', err) // eslint-disable-line
      );
  };

  const onAsyncChange = (field, obj) => {
    // console.log(field, obj);
    const country = obj.provincia === 'EE';
    if (country) {
      onChange(name, obj.comune);
      onChange(provincia, obj.provincia);
      onChange('regioneNascita', '');
    } else {
      onChange(name, obj.comune);
      onChange(provincia, obj.provincia);
      onChange('regioneNascita', obj.regione);
    }
  };

  return (
    <Form.Field error={Boolean(touched && error)}>
      <label htmlFor={name}>
        {label}
        <Required required={required} />{' '}
        {tooltip ? (
          <Popup
            className="fieldTooltip"
            style={{ backgroundColor: '#333d68' }}
            inverted
            trigger={<Icon size="small" circular name="question" />}
          >
            {tooltip}
          </Popup>
        ) : (
          ''
        )}
      </label>
      <AsyncSelect
        key={initialValue}
        loadOptions={_.debounce(getOptions, 250)}
        value={{ label: initialValue, value: initialValue }}
        onChange={onAsyncChange}
        onBlur={handleBlur}
        error={error}
        touched={touched}
        name={name}
        disabled={disabled}
      />
      {touched && error && <ErrorMessage>{error}</ErrorMessage>}
    </Form.Field>
  );
};

export const ComuneResidenza = ({
  onChange,
  onBlur,
  error,
  name,
  label,
  initialValue,
  required,
  tooltip,
  disabled,
}) => {
  const getOptions = (input, cb) => {
    ComuniService.getComuniOptions(input || initialValue)
      .then((res) => cb(null, res))
      .catch(
        (err) => console.log('ComuneResidenza: ', err) // eslint-disable-line
      );
  };

  const onAsyncChange = (field, obj) => {
    if (obj) {
      const newState = {
        comune: obj.value,
        provincia: obj.provincia,
        regione: obj.regione,
        cap: '',
      };

      onChange(newState);
    }
  };

  return (
    <Form.Field>
      <label htmlFor={name}>
        {label}
        <Required required={required} />{' '}
        {!!tooltip && (
          <Popup
            className="fieldTooltip"
            style={{ backgroundColor: '#333d68' }}
            inverted
            trigger={<Icon size="small" circular name="question" />}
          >
            {tooltip}
          </Popup>
        )}
      </label>
      <AsyncSelect
        key={initialValue}
        loadOptions={_.debounce(getOptions, 250)}
        value={{ label: initialValue, value: initialValue }}
        onChange={onAsyncChange}
        {...{ onBlur, error, name, disabled }}
      />
    </Form.Field>
  );
};

export const NazioneResidenza = ({ onChange, onBlur, error, name, label, initialValue, required, tooltip }) => {
  const getOptions = (input, cb) => {
    NazioniService.getNazioniOptions(input || initialValue)
      .then((res) => {
        cb(null, res);
      })
      .catch(
        (err) => console.log('NazioneResidenza: ', err) // eslint-disable-line
      );
  };

  const onAsyncChange = (field, obj) => {
    if (obj) {
      onChange(field, obj.value);
    }
  };

  return (
    <Form.Field>
      <label htmlFor={name}>
        {label}
        <Required required={required} />{' '}
        {tooltip ? (
          <Popup
            className="fieldTooltip"
            style={{ backgroundColor: '#333d68' }}
            inverted
            trigger={<Icon size="small" circular name="question" />}
          >
            {tooltip}
          </Popup>
        ) : (
          ''
        )}
      </label>
      <AsyncSelect
        key={initialValue}
        loadOptions={_.debounce(getOptions, 250)}
        value={{ label: initialValue, value: initialValue }}
        onChange={onAsyncChange}
        onBlur={onBlur}
        error={error}
        name={name}
      />
    </Form.Field>
  );
};

export const SuggestComuneString = ({
  onChange,
  onBlur,
  touched,
  error,
  name,
  label,
  initialValue,
  onlyItaly = false,
}) => {
  const handleChange = (value) => {
    // console.log("SuggestComune: ", value);
    if (!value.gmaps) return undefined;

    const components = value.gmaps.address_components;
    const comune =
      _.get(_.filter(components, (item) => item.types[0] === 'administrative_area_level_3')[0], 'long_name') ||
      _.get(_.filter(components, (item) => item.types[0] === 'locality')[0], 'long_name');
    const provincia = VoE(
      _.filter(components, (item) => item.types[0] === 'administrative_area_level_2')[0],
      'short_name'
    );
    onChange(name, `${comune} (${provincia})`);

    return undefined;
  };

  const handleBlur = () => {
    onBlur(name, true);
  };

  const country = onlyItaly ? 'it' : null;

  return (
    <Form.Field error={Boolean(touched && error)}>
      <label htmlFor={name}>{label}</label>
      <StyledSuggest
        autoActivateFirstSuggest
        initialValue={initialValue}
        onBlur={handleBlur}
        onSuggestSelect={handleChange}
        country={country}
      />
      {touched && error && <ErrorMessage>{error}</ErrorMessage>}
    </Form.Field>
  );
};

export class SuggestIndirizzo extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      search: 'auto', // or 'manual'
    };
  }

  changeMode = (val) => {
    this.setState({ search: val });
  };

  handleChange = (value) => {
    if (!value.gmaps) return undefined;
    const latitudine = value.gmaps.geometry.location.lat();
    const longitudine = value.gmaps.geometry.location.lng();
    const components = value.gmaps.address_components;
    const comune =
      _.get(_.filter(components, (item) => item.types[0] === 'administrative_area_level_3')[0], 'long_name') ||
      _.get(_.filter(components, (item) => item.types[0] === 'locality')[0], 'long_name');
    const provincia = VoE(
      _.filter(components, (item) => item.types[0] === 'administrative_area_level_2')[0],
      'short_name'
    );
    const regione = VoE(
      _.filter(components, (item) => item.types[0] === 'administrative_area_level_1')[0],
      'long_name'
    );
    const numero = VoE(_.filter(components, (item) => item.types[0] === 'street_number')[0], 'long_name');
    const via = VoE(_.filter(components, (item) => item.types[0] === 'route')[0], 'long_name');
    const cap = VoE(_.filter(components, (item) => item.types[0] === 'postal_code')[0], 'long_name');
    const nazione = VoE(_.filter(components, (item) => item.types[0] === 'country')[0], 'long_name');
    const indirizzoCompleto = value.gmaps.formatted_address;

    const indirizzo = `${via}`.trim();

    const items = _.cloneDeep(this.props.elements);

    // Se è un array di indirizzi
    if (!this.props.flat) {
      if (this.props.remove) {
        // This is a custom props for Candidate
        _.remove(items, {
          tipo: this.props.name,
        });
      }

      items.push({
        tipo: this.props.name,
        comune,
        provincia,
        regione,
        indirizzo,
        numeroCivico: numero,
        cap,
        indirizzoCompleto,
        nazione,
        latitudine,
        longitudine,
      });

      this.props.onChange('indirizzi', items);
    } else {
      // Se è un indirizzo piano
      this.props.onChange('indirizzoCompleto', indirizzoCompleto);
      this.props.onChange('comune', comune);
      this.props.onChange('provincia', provincia);
      this.props.onChange('regione', regione);
      this.props.onChange('cap', cap);
      this.props.onChange('indirizzo', indirizzo);
      this.props.onChange('nazione', nazione);
      this.props.onChange('numeroCivico', numero);
    }

    return undefined;
  };

  handleBlur = () => {
    this.props.onBlur(this.props.name, true);
  };

  handleManualChange = (values) => {
    const comune = values.comune || '';
    const provincia = values.provincia || '';
    const regione = values.regione || '';
    const indirizzo = values.indirizzo || '';
    const cap = values.cap || '';
    const nazione = values.nazione || '';
    const numeroCivico = values.numeroCivico || '';

    const indirizzoCompleto = `${indirizzo} ${numeroCivico}, ${cap} ${comune} ${provincia}${
      nazione.trim() ? ', ' : ''
    }${nazione}`.trim();

    const items = _.cloneDeep(this.props.elements);

    // Se è un array di indirizzi
    if (!this.props.flat) {
      if (this.props.remove) {
        _.remove(items, {
          tipo: this.props.name,
        });
      }

      items.push({
        tipo: this.props.name,
        comune,
        provincia,
        regione,
        indirizzo,
        cap,
        indirizzoCompleto,
        nazione,
        numeroCivico,
      });

      this.props.onChange('indirizzi', items);
    } else {
      // Se è un indirizzo piano
      this.props.onChange('indirizzoCompleto', indirizzoCompleto);
      this.props.onChange('comune', comune);
      this.props.onChange('provincia', provincia);
      this.props.onChange('regione', regione);
      this.props.onChange('cap', cap);
      this.props.onChange('indirizzo', indirizzo);
      this.props.onChange('nazione', nazione);
    }
  };

  render() {
    const { disabled, elements, error, flat, geocoords, initialValue, label, name, required } = this.props;
    const { search } = this.state;

    return (
      <div>
        <Form.Field error={Boolean(error)}>
          <label htmlFor={name} style={{ display: 'inline-block' }}>
            {label}
            <Required required={required} />
          </label>
          {!(search === 'auto' && geocoords) && <SwitchMode switch={this.changeMode} mode={search} />}
          {(search === 'auto' && [
            <StyledSuggest
              autoActivateFirstSuggest
              onBlur={this.handleBlur}
              initialValue={initialValue}
              onSuggestSelect={this.handleChange}
              disabled={disabled}
            />,
            error && <ErrorMessage>{error}</ErrorMessage>,
          ]) || (
            <GeoInput
              handleChange={this.handleManualChange}
              flat={flat}
              elements={elements}
              type={name}
              disabled={disabled}
            />
          )}
        </Form.Field>
      </div>
    );
  }
}

SuggestComuneNascita.propTypes = {
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  touched: PropTypes.bool,
  error: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  tooltip: PropTypes.string,
  initialValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  required: PropTypes.bool,
  disabled: PropTypes.bool,
};

ComuneResidenza.propTypes = {
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  name: PropTypes.string,
  label: PropTypes.string,
  tooltip: PropTypes.string,
  error: PropTypes.string,
  initialValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  required: PropTypes.bool,
  disabled: PropTypes.bool,
};

NazioneResidenza.propTypes = {
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  name: PropTypes.string,
  label: PropTypes.string,
  tooltip: PropTypes.string,
  error: PropTypes.string,
  initialValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  required: PropTypes.bool,
};

SuggestComuneString.propTypes = {
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  touched: PropTypes.bool,
  error: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  initialValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onlyItaly: PropTypes.bool,
};

SuggestIndirizzo.propTypes = {
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  error: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  initialValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  elements: PropTypes.array,
  flat: PropTypes.bool,
  required: PropTypes.bool,
  geocoords: PropTypes.bool,
  remove: PropTypes.bool,
  disabled: PropTypes.bool,
};

SuggestIndirizzo.defaultProps = {
  remove: true,
};



// WEBPACK FOOTER //
// ./app/components/Forms/UI/GeoSuggest/index.js